import { useRef } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import './StayYourWaySection.css';

gsap.registerPlugin(useGSAP, ScrollTrigger);

const reasons = [
  { number: '01', title: 'Time to unwind', text: 'Make room for a slower pace, a fresh view and a change of scene.' },
  { number: '02', title: 'Room to recharge', text: 'Enjoy a comfortable base between local experiences and quiet moments.' },
  { number: '03', title: 'A stop along the way', text: 'Break up the journey and explore a little more before moving on.' },
];

export default function StayYourWaySection({ ready = true }) {
  const scope = useRef(null);
  const leafRef = useRef(null);
  const leafWrapRef = useRef(null);

  useGSAP(() => {
    if (!ready) return;
    if (!scope.current || !leafRef.current || !leafWrapRef.current) return;
    const media = gsap.matchMedia();
    media.add('(prefers-reduced-motion: no-preference)', () => {
      const leaf = leafRef.current;
      const leafWrap = leafWrapRef.current;
      const reveal = gsap.timeline({
        scrollTrigger: { trigger: scope.current, start: 'top 75%', toggleActions: 'play none none none' },
        defaults: { ease: 'power3.out' },
      });
      reveal
        .from('.stay-way__eyebrow', { autoAlpha: 0, y: 20, duration: 0.7 })
        .from('.stay-way__heading', { autoAlpha: 0, y: 35, duration: 0.9 }, '-=.45')
        .from('.stay-way__card', { autoAlpha: 0, y: 40, duration: 0.7, stagger: 0.12 }, '-=.55');
      gsap.fromTo(leaf,
        { autoAlpha: 0, y: 12, scale: 0.95 },
        { autoAlpha: 1, y: 0, scale: 1, ease: 'power3.out',
          scrollTrigger: { trigger: scope.current, start: 'top 82%', end: 'top 35%', scrub: 1 } });
      gsap.to(leaf, {
        yPercent: -10,
        rotate: 2,
        ease: 'none',
        scrollTrigger: { trigger: scope.current, start: 'top bottom', end: 'bottom top', scrub: 1 },
      });
      gsap.to(leafWrap, {
        x: 5, y: -5, rotation: 2,
        transformOrigin: 'center center',
        ease: 'none',
        scrollTrigger: {
          trigger: scope.current,
          start: 'top 80%',
          end: 'bottom 20%',
          scrub: 1,
          invalidateOnRefresh: true,
        },
      });
    });
    return () => media.revert();
  }, { scope, dependencies: [ready], revertOnUpdate: true });

  return (
    <section ref={scope} className="stay-way" aria-labelledby="stay-way-heading">
      <div className="stay-way__inner">
        <div className="stay-way__top">
          <div>
            <p className="stay-way__eyebrow">Stay your way</p>
            <h2 className="stay-way__heading" id="stay-way-heading">For a getaway.<br />For everyday life.</h2>
          </div>
          <div ref={leafWrapRef} className="stay-way__leaf-wrap">
            <img ref={leafRef} className="stay-way__leaf" src={`${process.env.PUBLIC_URL}/images/hummingbird.svg`} alt="" aria-hidden="true" width="420" height="300" />
          </div>
        </div>
        <div className="stay-way__cards">
          {reasons.map(reason => (
            <article className="stay-way__card" key={reason.number}>
              <span className="stay-way__number">{reason.number}</span>
              <h3>{reason.title}</h3>
              <p>{reason.text}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
